import { format as formatDate } from "date-fns";
import type { Immutable } from "immer";
import React from "react";
import type { ILocationGuessResponse } from "tsi-common-react/src/models/location";
import { getDinero } from "tsi-common-react/src/utils/money";
import { t } from "ttag";

import shipping from "../../utils/shipping";

const MONEY_FORMAT = "$0,0.00";

type ShippingLocalizationState = {
    arrivalDate?: Date;
    daysInTransit?: number;
    locationDisplayName?: string;
    shippingCost?: string;
    zip?: string;
};

type ShippingLocalization = ShippingLocalizationState & {
    setUserZip: (zip: string) => Promise<boolean>;
};

const getLocationDisplayName = (
    location: ILocationGuessResponse,
    responseLocationName?: string,
): string => responseLocationName || location.zip || "";

const getShippingCost = (shippingCost: string): string => {
    const cost = getDinero(shippingCost);
    return cost.isZero() ? t`FREE` : cost.toFormat(MONEY_FORMAT);
};

export const useShippingLocalization = (): ShippingLocalization => {
    const [state, setState] = React.useState<ShippingLocalizationState>({});
    const isMountedRef = React.useRef(false);
    const activeZipRef = React.useRef<string | null>(null);
    const resolvedZipRef = React.useRef<string | null>(null);

    const updateUserLocation = React.useCallback(
        async (location: ILocationGuessResponse | null): Promise<boolean> => {
            if (!location?.zip) {
                return false;
            }
            if (
                location.zip === activeZipRef.current &&
                location.zip === resolvedZipRef.current
            ) {
                return true;
            }
            activeZipRef.current = location.zip;
            const response = await shipping.getShipmentArrivalDate(
                location.zip,
            );
            if (!response) {
                if (activeZipRef.current === location.zip) {
                    activeZipRef.current = null;
                }
                return false;
            }
            if (activeZipRef.current !== location.zip) {
                return false;
            }
            if (isMountedRef.current) {
                resolvedZipRef.current = location.zip;
                setState({
                    arrivalDate: response.arrives_on,
                    daysInTransit: response.days_in_transit,
                    locationDisplayName: getLocationDisplayName(
                        location,
                        response.location_name,
                    ),
                    shippingCost: getShippingCost(response.shipping_cost),
                    zip: location.zip,
                });
            }
            return true;
        },
        [],
    );

    React.useEffect(() => {
        isMountedRef.current = true;
        const handleLocationChange = (
            location: ILocationGuessResponse,
        ): void => {
            void updateUserLocation(location);
        };
        shipping.on("change", handleLocationChange);
        void shipping.getClientLocation().then(updateUserLocation);
        return () => {
            isMountedRef.current = false;
            shipping.removeListener("change", handleLocationChange);
        };
    }, [updateUserLocation]);

    const setUserZip = React.useCallback(
        async (zip: string): Promise<boolean> => {
            shipping.setPendingClientLocation(zip);
            try {
                const updated = await updateUserLocation({ zip });
                if (updated) {
                    shipping.setClientLocation(zip);
                }
                return updated;
            } finally {
                shipping.clearPendingClientLocation(zip);
            }
        },
        [updateUserLocation],
    );

    return {
        ...state,
        setUserZip,
    };
};

export const EditableUserLocation = (
    props: Immutable<{
        localization: ShippingLocalization;
    }>,
): React.JSX.Element => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [isUpdating, setIsUpdating] = React.useState(false);
    const [zip, setZip] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement>(null);
    const updateInFlightRef = React.useRef(false);

    React.useEffect(() => {
        if (isEditing) {
            setZip(props.localization.zip ?? "");
            inputRef.current?.focus();
        }
    }, [isEditing, props.localization.zip]);

    const update = async (): Promise<void> => {
        if (zip.length < 5 || updateInFlightRef.current) {
            return;
        }
        updateInFlightRef.current = true;
        setIsUpdating(true);
        try {
            const updated = await props.localization.setUserZip(zip);
            if (updated) {
                setIsEditing(false);
            } else {
                inputRef.current?.focus();
            }
        } finally {
            updateInFlightRef.current = false;
            setIsUpdating(false);
        }
    };

    return (
        <>
            <a
                href="#"
                className="l10n-fill-user-location l10n-editable"
                style={isEditing ? { display: "none" } : undefined}
                onClick={(event) => {
                    event.preventDefault();
                    setIsEditing(true);
                }}
            >
                {props.localization.locationDisplayName ?? t`Enter Zip Code`}
            </a>
            {isEditing ? (
                <input
                    ref={inputRef}
                    className="l10n-zip-input"
                    disabled={isUpdating}
                    id="location-zip-code"
                    placeholder={t`ZIP CODE`}
                    value={zip}
                    onBlur={() => {
                        void update();
                    }}
                    onChange={(event) => {
                        setZip(event.target.value);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            void update();
                        }
                    }}
                />
            ) : null}
        </>
    );
};

export const ShippingArrivalDate = (
    props: Immutable<{
        localization: ShippingLocalization;
        dateFormat?: string;
        prefix?: string;
    }>,
): React.JSX.Element => {
    const date = props.localization.arrivalDate;
    return (
        <span
            className="l10n-fill-shipping-arrival-date"
            data-format={props.dateFormat ?? "MMMM do"}
            data-prefix={props.prefix}
        >
            {date
                ? `${props.prefix ?? ""}${formatDate(
                      date,
                      props.dateFormat ?? "MMMM do",
                  )}`
                : ""}
        </span>
    );
};
